/**
 * useOrganizationWorkflowContext — the organization's name, its holdings, and
 * the provenance of its current workflow (DC-1802).
 *
 * Two surfaces need these facts and neither had them:
 *  - the Settings → Organization Workflow confirmation, which has to state
 *    what an administrator is about to do to a populated workspace before
 *    they do it, and who last changed the workflow;
 *  - the shell notice a NON-ADMIN sees after a flip, which has to say what
 *    happened, who did it, and who can undo it.
 *
 * The read rides `GET /api/experience-profile/context` — the caller's own
 * organization only, derived server-side from the verified token. It carries
 * NO profile key or version: profile resolution stays on
 * `useExperienceProfile` / `GET /api/iam/me` alone (§5.5 rule 2), and this
 * hook must never become a second source of it.
 *
 * Like the profile hook, this subscribes to the experience-profile refresh
 * boundary: an administrator changing the workflow advances the org epoch,
 * which makes the cached GET unreachable and re-reads fresh provenance in
 * the active session.
 *
 * Failure is 'unavailable', never a fabricated zero. "0 datasets" and "we
 * could not read your datasets" are different statements, and a guard that
 * silently downgrades the second into the first is the exact defect this
 * story exists to fix.
 */

import { useEffect, useRef, useState } from 'react';
import { useApiClient } from './useApiClient';
import {
  getExperienceProfileRefreshEpoch,
  subscribeExperienceProfileRefresh,
} from './experienceProfileRefresh';

export interface WorkflowProvenance {
  changedAt: string;
  changedByName: string | null;
  changedByEmail: string | null;
}

export interface WorkflowHoldings {
  datasets: number;
  analyses: number;
}

export interface OrganizationWorkflowContext {
  organizationName: string;
  holdings: WorkflowHoldings;
  provenance: WorkflowProvenance | null;
}

export type WorkflowContextState =
  | { status: 'pending' }
  | { status: 'ready'; context: OrganizationWorkflowContext }
  | { status: 'unavailable' };

interface ContextEnvelope {
  data?: {
    organizationName?: string;
    holdings?: { datasets?: number; analyses?: number };
    provenance?: {
      changedAt?: string;
      changedByName?: string | null;
      changedByEmail?: string | null;
    } | null;
  };
}

export function useOrganizationWorkflowContext(): WorkflowContextState {
  // Depend on the destructured accessor, never the client object — the bare
  // object is a new identity every render and loops the effect.
  const { get, isAuthenticated } = useApiClient();
  const [state, setState] = useState<WorkflowContextState>({ status: 'pending' });
  const [refreshEpoch, setRefreshEpoch] = useState(getExperienceProfileRefreshEpoch);
  const lastRefreshEpochRef = useRef(refreshEpoch);

  useEffect(
    () =>
      subscribeExperienceProfileRefresh(() => {
        setRefreshEpoch(getExperienceProfileRefreshEpoch());
      }),
    []
  );

  useEffect(() => {
    let cancelled = false;

    if (!isAuthenticated) {
      setState({ status: 'pending' });
      return;
    }

    if (lastRefreshEpochRef.current !== refreshEpoch) {
      lastRefreshEpochRef.current = refreshEpoch;
      setState({ status: 'pending' });
    }

    (async () => {
      try {
        const body = await get<ContextEnvelope>('/api/experience-profile/context');
        if (cancelled) return;

        const data = body?.data;
        const datasets = data?.holdings?.datasets;
        const analyses = data?.holdings?.analyses;

        if (
          typeof data?.organizationName !== 'string' ||
          typeof datasets !== 'number' ||
          typeof analyses !== 'number'
        ) {
          setState({ status: 'unavailable' });
          return;
        }

        const rawProvenance = data.provenance;
        const provenance: WorkflowProvenance | null =
          rawProvenance && typeof rawProvenance.changedAt === 'string'
            ? {
                changedAt: rawProvenance.changedAt,
                changedByName: rawProvenance.changedByName ?? null,
                changedByEmail: rawProvenance.changedByEmail ?? null,
              }
            : null;

        setState({
          status: 'ready',
          context: {
            organizationName: data.organizationName,
            holdings: { datasets, analyses },
            provenance,
          },
        });
      } catch {
        if (!cancelled) setState({ status: 'unavailable' });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [get, isAuthenticated, refreshEpoch]);

  return state;
}
